import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, LucideIcon } from 'lucide-react';
import { FadeIn, StaggerContainer } from '@/components/Animations';
import ServiceCard from '@/components/ServiceCard';
import { useTranslation } from 'react-i18next';

interface BadgeSectionProps {
  title: string;
  icon: LucideIcon;
  services: any[];
  loading?: boolean;
  emptyMessage?: string;
  prepareServiceForCard: (service: any) => any;
  badgeColor?: string;
}

export const BadgeSection: React.FC<BadgeSectionProps> = ({
  title,
  icon: Icon,
  services,
  loading = false,
  emptyMessage,
  prepareServiceForCard,
  badgeColor
}) => {
  const { t } = useTranslation();

  if (!loading && services.length === 0) {
    return null; // Don't render empty sections
  }

  return (
    <section className="py-3 lg:py-12 px-4 md:px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-4 lg:mb-6">
          <FadeIn direction="up">
            <div className="flex items-center space-x-3 icon-heading">
              <h2 className="text-lg md:text-2xl font-semibold text-gray-900">
                {title}
              </h2>
              <div 
                className="flex items-center justify-center w-8 h-8 rounded-full"
                style={{
                  backgroundColor: badgeColor ? `${badgeColor}20` : `hsl(var(--primary) / 0.1)`
                }}
              >
                <Icon 
                  className="w-4 h-4 md:w-5 md:h-5"
                  style={{ color: badgeColor || `hsl(var(--primary))` }}
                />
              </div>
            </div>
          </FadeIn>
          <FadeIn direction="up">
            <Link to="/services">
              <Button variant="ghost" className="text-sm group font-medium">
                {t('home.service.viewall')}
                <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </FadeIn>
        </div>
        
        {/* Mobile horizontal scroll */}
        <div className="md:hidden">
          <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2" style={{ WebkitOverflowScrolling: 'touch' }}>
            {loading ? (
              Array(3).fill(0).map((_, index) => (
                <div key={index} className="flex-none w-[240px] bg-card rounded-2xl overflow-hidden shadow-sm border border-border animate-pulse">
                  <div className="h-28 bg-muted"></div>
                  <div className="p-3.5 space-y-2">
                    <div className="h-5 bg-muted rounded w-3/4"></div>
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                    <div className="h-4 bg-muted rounded w-1/4"></div>
                  </div>
                </div>
              ))
            ) : services.length > 0 ? (
              <>
                {services.map((service, index) => (
                  <div key={service.id} className="flex-none w-[240px]">
                    <ServiceCard
                      {...prepareServiceForCard(service)}
                      delay={index * 0.1}
                      isMobile={true}
                    />
                  </div>
                ))}
              </>
            ) : (
              <div className="flex-none w-full text-center py-12">
                <p className="text-muted-foreground">{emptyMessage || t('home.services.no.services')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Desktop grid layout */}
        <div className="hidden md:block">
          <StaggerContainer className="grid grid-cols-3 gap-6 md:gap-8">
            {loading ? (
              Array(3).fill(0).map((_, index) => (
                <div key={index} className="bg-card rounded-2xl overflow-hidden shadow-sm border border-border animate-pulse">
                  <div className="h-48 bg-muted"></div>
                  <div className="p-5 space-y-3">
                    <div className="h-5 bg-muted rounded w-3/4"></div>
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                    <div className="h-4 bg-muted rounded w-1/4"></div>
                  </div>
                </div>
              ))
            ) : services.length > 0 ? (
              services.map((service, index) => (
                <ServiceCard
                  key={service.id}
                  {...prepareServiceForCard(service)}
                  delay={index * 0.1}
                />
              ))
            ) : (
              <div className="col-span-3 text-center py-12">
                <p className="text-muted-foreground">{emptyMessage || t('home.services.no.services')}</p>
              </div>
            )}
          </StaggerContainer>
        </div>
      </div>
    </section>
  );
};